import { newIdempotencyKey } from "@station/api-client";
import { createEvPartnerImportSchema, type EvPartnerImport } from "@station/contracts";
import { addDays, type LocalDate, toIstanbulDate } from "@station/core";
import {
  Alert,
  Button,
  Card,
  DataTable,
  type DataTableColumn,
  DateRangePicker,
  Dialog,
  EmptyState,
  FormField,
  MoneyText,
  Select,
  SkeletonRows,
  StatusBadge,
  useToast,
} from "@station/ui";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Import } from "lucide-react";
import { type ReactElement, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { formatKwh, istanbulIso, istanbulParts } from "../../../features/ev/ev-values.js";
import { useEvPartnerImports, useEvPermissions, useRefreshEv } from "../../../features/ev/queries.js";
import { EvPage, type EvStationOption, useEvStation } from "../../../features/ev/shared.js";
import { compactSearch } from "../../../features/shifts/shift-values.js";
import { formIssues } from "../../../features/wash/wash-values.js";
import { api } from "../../../lib/api.js";
import { useCursorPages } from "../../../lib/cursor-pages.js";
import { errorMessage } from "../../../lib/errors.js";
import { formatDate, formatDateTime } from "../../../lib/format.js";

const searchSchema = z.object({
  station: z.string().uuid().optional().catch(undefined),
  filter: z.string().uuid().optional().catch(undefined),
});
type Search = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/_app/sarj/ag-aktarimlari")({
  validateSearch: (search) => searchSchema.parse(search),
  component: PartnerImportsRoute,
});

/**
 * Şarj ağı (ortak) içe aktarımları (Prompt 19 web, #76): `GET /ev/partner-imports` — dönem, içe aktarılan / atlanan
 * (ortak oturum kimliğiyle tekil) oturum sayısı, kWh, tutar (istasyon geliri DEĞİL), uyarılar (sahte adaptör). "İçe aktar"
 * (`POST /ev/stations/{id}/partner-imports`, `ev.operate`) yalnızca şarj ağı işletmecisi modelindeki istasyonlarda; aralık
 * en çok 31 gün, İstanbul günleriyle (bitiş günü dâhil).
 */
function PartnerImportsRoute(): ReactElement {
  const { t } = useTranslation();
  const permissions = useEvPermissions();
  const selection = useEvStation(permissions.enabled && permissions.canRead);
  const partnerStations = selection.stations.filter(
    (station) => station.operationModel === "NETWORK_PARTNER",
  );
  const [open, setOpen] = useState(false);
  return (
    <EvPage
      title={t("menu.evPartnerImports")}
      description={t("ev.imports.description")}
      actions={
        permissions.canOperate && partnerStations.length > 0 ? (
          <Button onClick={() => setOpen(true)}>
            <Import aria-hidden="true" />
            {t("ev.imports.run")}
          </Button>
        ) : null
      }
    >
      {selection.isLoading ? (
        <SkeletonRows rows={5} />
      ) : (
        <>
          {partnerStations.length === 0 ? (
            <Alert tone="info">{t("ev.imports.noPartnerStations")}</Alert>
          ) : null}
          <ImportsBody stations={selection.stations} />
          <ImportDialog open={open} onOpenChange={setOpen} stations={partnerStations} />
        </>
      )}
    </EvPage>
  );
}

/**
 * Dönem: iki uç da İstanbul gece yarısıysa gün aralığı (bitiş günü dâhil — sunucuda bitiş anı hariçtir), değilse anlar.
 */
function periodText(row: Pick<EvPartnerImport, "periodFrom" | "periodTo">): string {
  const from = istanbulParts(row.periodFrom);
  const to = istanbulParts(row.periodTo);
  if (from.time === "00:00" && to.time === "00:00")
    return `${formatDate(from.date)} – ${formatDate(addDays(to.date, -1))}`;
  return `${formatDateTime(row.periodFrom)} – ${formatDateTime(row.periodTo)}`;
}

function ImportsBody({ stations }: { stations: EvStationOption[] }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const setSearch = (patch: Partial<Search>) =>
    void navigate({
      to: ".",
      search: ((previous: Record<string, unknown>) => compactSearch({ ...previous, ...patch })) as never,
      replace: true,
    });
  const pages = useCursorPages(search.filter ?? "all");
  const list = useEvPartnerImports(search.filter, pages.cursor);

  const columns = useMemo<DataTableColumn<EvPartnerImport>[]>(
    () => [
      {
        id: "createdAt",
        header: t("ev.imports.createdAt"),
        hideable: false,
        exportValue: (row) => row.createdAt,
        cell: (row) => <span className="tabular whitespace-nowrap">{formatDateTime(row.createdAt)}</span>,
      },
      {
        id: "station",
        header: t("ev.fields.station"),
        exportValue: (row) => row.stationName,
        cell: (row) => (
          <span className="grid gap-0.5">
            <span>{row.stationName}</span>
            <span className="font-mono text-xs text-muted-foreground">{row.partnerCode}</span>
          </span>
        ),
      },
      {
        id: "period",
        header: t("ev.imports.period"),
        exportValue: (row) => `${row.periodFrom} – ${row.periodTo}`,
        cell: (row) => <span className="text-xs tabular whitespace-nowrap">{periodText(row)}</span>,
      },
      {
        id: "status",
        header: t("common.status"),
        exportValue: (row) => row.status,
        cell: (row) => (
          <span className="grid justify-items-start gap-1">
            <StatusBadge tone={row.status === "COMPLETED" ? "success" : "danger"}>
              {t(`ev.imports.status.${row.status}`)}
            </StatusBadge>
            {row.warnings.map((warning) => (
              <span key={warning} className="text-xs text-warning">
                {warning === "MOCK_ADAPTER" ? t("ev.imports.mockAdapter") : warning}
              </span>
            ))}
            {row.error ? <span className="text-xs text-destructive">{row.error}</span> : null}
          </span>
        ),
      },
      {
        id: "counts",
        header: t("ev.imports.counts"),
        align: "right",
        exportValue: (row) => `${row.importedCount}/${row.skippedCount}`,
        cell: (row) => (
          <span className="grid justify-items-end gap-0.5 text-xs tabular">
            <span>{t("ev.imports.imported", { count: row.importedCount })}</span>
            <span className="text-muted-foreground">
              {t("ev.imports.skipped", { count: row.skippedCount })}
            </span>
          </span>
        ),
      },
      {
        id: "energy",
        header: t("ev.fields.energy"),
        align: "right",
        exportValue: (row) => row.totalEnergyKwh,
        cell: (row) => <span className="tabular whitespace-nowrap">{formatKwh(row.totalEnergyKwh)}</span>,
      },
      {
        id: "amount",
        header: t("ev.imports.partnerAmount"),
        align: "right",
        exportValue: (row) => Number(row.totalAmount) / 100,
        cell: (row) => <MoneyText value={row.totalAmount} />,
      },
      {
        id: "sessions",
        header: t("ev.imports.sessions"),
        cell: (row) => (
          <Link
            to="/sarj/oturumlar"
            search={
              {
                station: row.stationId,
                source: "PARTNER",
                from: toIstanbulDate(new Date(row.periodFrom)),
                to: addDays(toIstanbulDate(new Date(row.periodTo)), -1),
              } as never
            }
            className="text-sm whitespace-nowrap underline underline-offset-4"
            aria-label={t("ev.imports.sessionsFor", { station: row.stationName })}
          >
            {t("ev.imports.openSessions")}
          </Link>
        ),
      },
    ],
    [t],
  );

  return (
    <>
      {stations.length > 1 ? (
        <Card className="flex flex-wrap items-end gap-3 p-4">
          <FormField label={t("ev.fields.station")} className="w-64">
            {(control) => (
              <Select
                {...control}
                value={search.filter ?? ""}
                onChange={(event) => setSearch({ filter: event.target.value || undefined })}
              >
                <option value="">{t("common.all")}</option>
                {stations.map((station) => (
                  <option key={station.id} value={station.id}>
                    {station.code} {station.name}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
        </Card>
      ) : null}
      <DataTable
        caption={t("ev.imports.caption")}
        columns={columns}
        rows={list.data?.items ?? []}
        rowKey={(row) => row.id}
        loading={list.isPending}
        error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
        emptyState={
          <EmptyState
            headingLevel={2}
            icon={<Import />}
            title={t("ev.imports.emptyTitle")}
            description={t("ev.imports.emptyDescription")}
          />
        }
        pagination={{
          hasPrevious: pages.hasPrevious,
          hasNext: list.data?.page.hasMore ?? false,
          onPrevious: pages.previous,
          onNext: () => pages.next(list.data?.page.nextCursor),
          pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
        }}
      />
    </>
  );
}

function ImportDialog({
  open,
  onOpenChange,
  stations,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  stations: EvStationOption[];
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshEv();
  const today = toIstanbulDate(new Date());
  const [stationId, setStationId] = useState("");
  const [range, setRange] = useState<{ from: LocalDate | null; to: LocalDate | null }>({
    from: addDays(today, -7),
    to: addDays(today, -1),
  });
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!open) return;
    const now = toIstanbulDate(new Date());
    setStationId(stations[0]?.id ?? "");
    setRange({ from: addDays(now, -7), to: addDays(now, -1) });
    setError(null);
  }, [open, stations]);

  const run = useMutation({
    mutationFn: async (body: { from: string; to: string }) =>
      (
        await api.POST("/ev/stations/{stationId}/partner-imports", {
          params: { path: { stationId }, header: { "Idempotency-Key": newIdempotencyKey() } },
          body,
        })
      ).data!,
    onSuccess: async (result) => {
      await refresh();
      toast({
        tone: result.status === "COMPLETED" ? "success" : "danger",
        title: t("ev.imports.done", { imported: result.importedCount, skipped: result.skippedCount }),
      });
      onOpenChange(false);
    },
    onError: (caught) => setError(errorMessage(caught)),
  });

  function submit(): void {
    setError(null);
    if (!range.from || !range.to) {
      setError(t("ev.imports.rangeRequired"));
      return;
    }
    // Bitiş GÜNÜ dâhil: sunucuya ertesi günün İstanbul gece yarısı (hariç) gider.
    const body = { from: istanbulIso(range.from, "00:00"), to: istanbulIso(addDays(range.to, 1), "00:00") };
    const issues = formIssues(createEvPartnerImportSchema, body);
    const first = Object.values(issues)[0];
    if (first) {
      setError(first);
      return;
    }
    run.mutate(body);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => !run.isPending && onOpenChange(next)}
      title={t("ev.imports.dialogTitle")}
      description={t("ev.imports.dialogDescription")}
      dismissible={!run.isPending}
    >
      <form
        className="grid gap-4"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        {error ? <Alert tone="danger">{error}</Alert> : null}
        <FormField label={t("ev.fields.station")} required>
          {(control) => (
            <Select {...control} value={stationId} onChange={(event) => setStationId(event.target.value)}>
              {stations.map((station) => (
                <option key={station.id} value={station.id}>
                  {station.code} {station.name}
                </option>
              ))}
            </Select>
          )}
        </FormField>
        <DateRangePicker label={t("ev.imports.range")} value={range} onValueChange={setRange} />
        <p className="text-xs text-muted-foreground">{t("ev.imports.rangeHint")}</p>
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={run.isPending}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" loading={run.isPending} disabled={stationId === ""}>
            {t("ev.imports.submit")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
